import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useWorkspaceMembers } from "@/hooks/useWorkspaceMembers";
import { TeamOverview } from "@/components/team/TeamOverview";
import { SeatUsageIndicator } from "@/components/team/SeatUsageIndicator";
import { MembersList } from "@/components/team/MembersList";
import { InviteForm } from "@/components/team/InviteForm";
import { ActivityLog } from "@/components/team/ActivityLog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, Activity } from "lucide-react";

export default function Team() {
  const navigate = useNavigate();
  const { workspace, loading: workspaceLoading } = useWorkspace();
  const { members, invites, loading: membersLoading, refresh } = useWorkspaceMembers(workspace?.id);
  const [userId, setUserId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    async function checkAuth() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/');
        return;
      }
      setUserId(user.id);

      // Check user's role in the workspace
      // TODO: Move this to RLS policies for proper security
      if (workspace?.id) {
        const { data: membership } = await supabase
          .from('workspace_members')
          .select('role')
          .eq('workspace_id', workspace.id)
          .eq('user_id', user.id)
          .single();

        if (membership) {
          setUserRole(membership.role);
        }
      }
    }

    checkAuth();
  }, [navigate, workspace]);

  // Client-side access check (TODO: Implement proper RLS)
  const canAccessPage = userRole === 'owner' || userRole === 'admin';

  if (workspaceLoading || membersLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Lädt...</div>
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">Kein Workspace gefunden.</p>
          <Button onClick={() => navigate('/onboarding')}>
            Zum Onboarding
          </Button>
        </div>
      </div>
    );
  }

  if (!canAccessPage) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-semibold">Zugriff verweigert</h1>
          <p className="text-muted-foreground">
            Nur Inhaber und Admins können diese Seite sehen.
          </p>
          <Button onClick={() => navigate('/')}>Zurück zur Startseite</Button>
        </div>
      </div>
    );
  }

  const activeMembers = members.filter(m => m.seat_status === 'active');
  const usedSeats = activeMembers.length + invites.length;

  return (
    <div className="space-y-6 md:space-y-8 max-w-5xl pb-20 md:pb-6">
      <div className="grid gap-6 md:grid-cols-2">
        <TeamOverview
          workspaceName={workspace.name}
          planType={workspace.plan_type}
          maxSeats={workspace.max_seats}
        />
        <SeatUsageIndicator
          usedSeats={usedSeats}
          maxSeats={workspace.max_seats}
        />
      </div>

      <Tabs defaultValue="members" className="space-y-6">
        <TabsList className="grid w-full max-w-lg grid-cols-3">
          <TabsTrigger value="members" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Mitglieder
          </TabsTrigger>
          <TabsTrigger value="invite" className="flex items-center gap-2">
            Einladen
          </TabsTrigger>
          <TabsTrigger value="activity" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Aktivitäten
          </TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="space-y-6">
          <MembersList 
            members={members} 
            invites={invites}
            currentUserRole={userRole || undefined}
            onRefresh={refresh}
          />
        </TabsContent>

        <TabsContent value="invite" className="space-y-6">
          {userId && (
            <InviteForm
              workspaceId={workspace.id}
              userId={userId}
              onInviteCreated={refresh}
              usedSeats={usedSeats}
              maxSeats={workspace.max_seats}
            />
          )}
        </TabsContent>

        <TabsContent value="activity">
          <ActivityLog workspaceId={workspace.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
